import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Category, DocumentType, Formula } from "@/contexts/TaskContext";

// Cache times in milliseconds
const STALE_TIME = 1 * 60 * 1000; // 1 minute - more responsive to changes
const CACHE_TIME = 30 * 60 * 1000; // 30 minutes

// Query keys
export const categoryKeys = {
  all: ["categories"] as const,
  lists: () => [...categoryKeys.all, "list"] as const,
};

export const documentTypeKeys = {
  all: ["documentTypes"] as const,
  lists: () => [...documentTypeKeys.all, "list"] as const,
};

export const formulaKeys = {
  all: ["formulas"] as const,
  lists: () => [...formulaKeys.all, "list"] as const,
};

// Fetch categories
const fetchCategories = async (): Promise<Category[]> => {
  const { data, error } = await supabase
    .from("categories")
    .select("*")
    .order("name");

  if (error) throw error;
  
  // Sort alphabetically with Polish locale for proper ą, ć, ę handling
  return (data || []).sort((a, b) => 
    a.name.localeCompare(b.name, 'pl', { sensitivity: 'base' })
  );
};

// Fetch document types
const fetchDocumentTypes = async (): Promise<DocumentType[]> => {
  const { data, error } = await supabase
    .from("document_types")
    .select("*")
    .order("name");

  if (error) throw error;
  return data || [];
};

// Fetch formulas
const fetchFormulas = async (): Promise<Formula[]> => {
  const { data, error } = await supabase
    .from("formulas")
    .select("*")
    .order("name");

  if (error) throw error;
  return data || [];
};

// Hook to fetch categories with caching
export function useCategoriesQuery() {
  return useQuery({
    queryKey: categoryKeys.lists(),
    queryFn: fetchCategories,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });
}

// Hook to fetch document types with caching
export function useDocumentTypesQuery() {
  return useQuery({
    queryKey: documentTypeKeys.lists(),
    queryFn: fetchDocumentTypes,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });
}

// Hook to fetch formulas with caching
export function useFormulasQuery() {
  return useQuery({
    queryKey: formulaKeys.lists(),
    queryFn: fetchFormulas,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });
}

// Add document type mutation with optimistic update
export function useAddDocumentTypeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from("document_types")
        .insert({ name })
        .select()
        .single();

      if (error) throw error;
      return data as DocumentType;
    },
    onMutate: async (name) => {
      await queryClient.cancelQueries({ queryKey: documentTypeKeys.lists() });

      const previousTypes = queryClient.getQueryData<DocumentType[]>(documentTypeKeys.lists());

      const optimisticType: DocumentType = {
        id: `temp-${Date.now()}`,
        name,
      };

      queryClient.setQueryData<DocumentType[]>(documentTypeKeys.lists(), (old) =>
        old ? [...old, optimisticType].sort((a, b) => a.name.localeCompare(b.name)) : [optimisticType]
      );

      return { previousTypes };
    },
    onError: (err, name, context) => {
      if (context?.previousTypes) {
        queryClient.setQueryData(documentTypeKeys.lists(), context.previousTypes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: documentTypeKeys.lists() });
    },
  });
}

// Add formula mutation with optimistic update
export function useAddFormulaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const { data, error } = await supabase
        .from("formulas")
        .insert({ name, color })
        .select()
        .single();

      if (error) throw error;
      return data as Formula;
    },
    onMutate: async ({ name, color }) => {
      await queryClient.cancelQueries({ queryKey: formulaKeys.lists() });

      const previousFormulas = queryClient.getQueryData<Formula[]>(formulaKeys.lists());

      const optimisticFormula: Formula = {
        id: `temp-${Date.now()}`,
        name,
        color,
      };

      queryClient.setQueryData<Formula[]>(formulaKeys.lists(), (old) =>
        old ? [...old, optimisticFormula].sort((a, b) => a.name.localeCompare(b.name)) : [optimisticFormula]
      );

      return { previousFormulas };
    },
    onError: (err, variables, context) => {
      if (context?.previousFormulas) {
        queryClient.setQueryData(formulaKeys.lists(), context.previousFormulas);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: formulaKeys.lists() });
    },
  });
}

// Hook to invalidate all metadata caches
export function useInvalidateMetadata() {
  const queryClient = useQueryClient();

  return {
    invalidateCategories: () => queryClient.invalidateQueries({ queryKey: categoryKeys.all }),
    invalidateDocumentTypes: () => queryClient.invalidateQueries({ queryKey: documentTypeKeys.all }),
    invalidateFormulas: () => queryClient.invalidateQueries({ queryKey: formulaKeys.all }),
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: documentTypeKeys.all });
      queryClient.invalidateQueries({ queryKey: formulaKeys.all });
    },
  };
}
